import {useState} from 'react';

import type {OpenBillRow} from '../types';

export const useOpenBillsTable = (
	rows: OpenBillRow[],
	onSelectionChange?: (ids: Set<string>) => void,
	externalSelectedIds?: Set<string>,
	onViewDetails?: (row: OpenBillRow) => void,
) => {
	const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(new Set());
	const selectedIds = externalSelectedIds ?? internalSelectedIds;

	const isRowSelectable = (row: OpenBillRow) => !row.isSaoLucas && row.cdEspecieTitulo !== 70;

	const selectableRows = rows.filter(isRowSelectable);

	const isAllSelected =
		selectableRows.length > 0 && selectableRows.every(row => selectedIds.has(row.cdObrigacao));
	const isSomeSelected = selectedIds.size > 0;

	const setIds = (next: Set<string>) => {
		if (!externalSelectedIds) {
			setInternalSelectedIds(next);
		}
		onSelectionChange?.(next);
	};

	const handleSelectAll = () => {
		if (isAllSelected) {
			setIds(new Set<string>());
		} else {
			setIds(new Set(selectableRows.map(row => row.cdObrigacao)));
		}
	};

	const handleSelectRow = (id: string) => {
		const row = rows.find(r => r.cdObrigacao === id);
		if (row && !isRowSelectable(row)) return;
		const next = new Set(selectedIds);
		if (next.has(id)) {
			next.delete(id);
		} else {
			next.add(id);
		}
		setIds(next);
	};

	const handleViewDetails = (row: OpenBillRow) => {
		onViewDetails?.(row);
	};

	return {
		selectedIds,
		isAllSelected,
		isSomeSelected,
		handleSelectAll,
		handleSelectRow,
		isRowSelectable,
		handleViewDetails,
	};
};
